import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl, errorMessage } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { ArrowLeft, Upload, Save, Loader2, Image as ImageIcon, Trash2, Bot, Shield, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
    const { settings, refresh } = useSettings();
    const fileRef = useRef(null);
    const [name, setName] = useState(settings.name);
    const [tagline, setTagline] = useState(settings.tagline);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [aiPersona, setAiPersona] = useState("");
    const [aiExtraInfo, setAiExtraInfo] = useState("");
    const [whatsappNumber, setWhatsappNumber] = useState("");
    const [csNumbers, setCsNumbers] = useState("");
    const [adminNotifyNumber, setAdminNotifyNumber] = useState("");
    const [marqueeText, setMarqueeText] = useState("");
    const [rewatermarking, setRewatermarking] = useState(false);

    useEffect(() => {
        setName(settings.name);
        setTagline(settings.tagline);
        setWhatsappNumber(settings.whatsapp_number || "");
    }, [settings.name, settings.tagline, settings.whatsapp_number]);

    // Load AI fields from admin endpoint (not exposed in public /settings)
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/settings")
            .then(({ data }) => {
                if (cancelled) return;
                setAiPersona(data?.ai_persona || "");
                setAiExtraInfo(data?.ai_extra_info || "");
                if (data?.whatsapp_number) setWhatsappNumber(data.whatsapp_number);
                setCsNumbers(data?.cs_numbers || "");
                setAdminNotifyNumber(data?.admin_notify_number || "");
                setMarqueeText(data?.marquee_text || "");
            })
            .catch(() => { /* ignore — fresh install */ });
        return () => { cancelled = true; };
    }, []);

    const onLogoPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setLogoFile(f);
        setLogoPreview(URL.createObjectURL(f));
    };

    const removePending = () => {
        setLogoFile(null);
        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoPreview(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setSaving(true);
        try {
            await api.put("/admin/settings", {
                name: name.trim(),
                tagline: tagline.trim(),
                ai_persona: aiPersona,
                ai_extra_info: aiExtraInfo,
                whatsapp_number: whatsappNumber.trim(),
                cs_numbers: csNumbers.trim(),
                admin_notify_number: adminNotifyNumber.trim(),
                marquee_text: marqueeText.trim(),
            });
            if (logoFile) {
                const fd = new FormData();
                fd.append("file", logoFile);
                await api.post("/admin/settings/logo", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                removePending();
            }
            await refresh();
            setSuccess("Pengaturan tersimpan");
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-settings-page" className="mx-auto max-w-3xl px-5 py-8">
            <Link
                to="/admin"
                data-testid="back-to-admin-link"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
                <ArrowLeft size={14} /> Kembali ke Dashboard
            </Link>
            <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight">Pengaturan Toko</h1>
            <p className="text-sm text-muted-foreground">Ubah nama toko, tagline, dan logo yang tampil di seluruh katalog.</p>

            <form onSubmit={onSubmit} className="mt-8 grid gap-8 md:grid-cols-[1fr_240px]">
                <div className="space-y-5">
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nama Toko *</span>
                        <input
                            data-testid="settings-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Tagline / Subjudul</span>
                        <input
                            data-testid="settings-tagline-input"
                            value={tagline}
                            onChange={(e) => setTagline(e.target.value)}
                            placeholder="Katalog Digital · Scan & Lihat"
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                        />
                    </label>

                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nomor WhatsApp Toko (untuk Customer)</span>
                        <input
                            data-testid="settings-whatsapp-input"
                            value={whatsappNumber}
                            onChange={(e) => setWhatsappNumber(e.target.value)}
                            placeholder="6287718567888"
                            inputMode="tel"
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            <strong>Hanya 1 nomor.</strong> Wajib nomor yang terdaftar di <strong>Meta WhatsApp Business</strong> agar AI bot bisa balas otomatis. Tombol Chat WA di website akan mengarah ke nomor ini.
                        </p>
                    </label>

                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nomor CS Sales Manual (escalation)</span>
                        <input
                            data-testid="settings-cs-numbers-input"
                            value={csNumbers}
                            onChange={(e) => setCsNumbers(e.target.value)}
                            placeholder="0878-8504-7680, 0811-1010-6258"
                            inputMode="tel"
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            Boleh <strong>multi-nomor</strong> (pisah dengan koma, spasi, atau kata "atau"). Saat AI bot perlu eskalasi ke admin (mis. customer marah / pertanyaan kompleks), bot otomatis akan kirim link <code>wa.me</code> ke nomor-nomor ini sebagai opsi kontak langsung.
                        </p>
                    </label>

                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nomor WhatsApp Notifikasi Admin (opsional)</span>
                        <input
                            data-testid="settings-admin-notify-input"
                            value={adminNotifyNumber}
                            onChange={(e) => setAdminNotifyNumber(e.target.value)}
                            placeholder="Kosongkan untuk pakai Nomor Toko"
                            inputMode="tel"
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            Nomor yang menerima <strong>notifikasi order baru</strong> via WhatsApp. Bisa beda dari nomor toko (mis. WA pemilik / pengurus stok). Kalau dikosongkan, akan dikirim ke Nomor Toko.
                        </p>
                    </label>

                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Running Text Bawah (Marquee)</span>
                        <textarea
                            data-testid="settings-marquee-input"
                            value={marqueeText}
                            onChange={(e) => setMarqueeText(e.target.value)}
                            placeholder="Contoh: 🔥 PROMO: Diskon 15% untuk pembelian di atas Rp 5jt • Free ongkir Jabodetabek • Kunjungi showroom kami di Jl. XYZ"
                            rows={2}
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors resize-y"
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            Teks berjalan yang muncul di <strong>bagian bawah semua halaman publik</strong> (bukan admin). Cocok untuk promo, jam operasional, atau pengumuman singkat. Kosongkan untuk sembunyikan. Pakai <code>•</code> sebagai pemisah antar-poin.
                        </p>
                    </label>

                    {/* AI Persona section */}
                    <div className="rounded-lg border border-accent/30 bg-accent/5 p-4 space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="grid h-8 w-8 place-items-center rounded-md bg-accent text-accent-foreground">
                                <Bot size={16} strokeWidth={1.75} />
                            </div>
                            <div>
                                <h2 className="font-heading text-base font-semibold">Persona AI WhatsApp</h2>
                                <p className="text-xs text-muted-foreground">Atur cara AI bot Anda berbicara ke customer.</p>
                            </div>
                        </div>
                        <label className="block">
                            <span className="label-eyebrow text-muted-foreground">Karakter / Persona</span>
                            <textarea
                                data-testid="settings-persona-input"
                                value={aiPersona}
                                onChange={(e) => setAiPersona(e.target.value)}
                                rows={3}
                                placeholder="Contoh: Ramah seperti sahabat dekat, sering pakai sapaan 'kak' atau 'sis', sabar menjelaskan."
                                className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                            />
                        </label>
                        <label className="block">
                            <span className="label-eyebrow text-muted-foreground">Info Tambahan Toko</span>
                            <textarea
                                data-testid="settings-extrainfo-input"
                                value={aiExtraInfo}
                                onChange={(e) => setAiExtraInfo(e.target.value)}
                                rows={5}
                                placeholder="Contoh: Jam operasional 09.00-21.00 WIB. Kebijakan retur 7 hari dengan struk. Ongkir Jabodetabek Rp 15.000. Cara pasang produk biasanya pakai obeng minus dan baut M6."
                                className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                AI akan pakai info ini saat menjawab pertanyaan customer di WhatsApp.
                            </p>
                        </label>
                    </div>

                    {error && (
                        <div data-testid="settings-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div data-testid="settings-success" className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-2 text-sm text-[hsl(var(--success))]">
                            {success}
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="settings-save-btn"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-all duration-200"
                    >
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} strokeWidth={1.75} />}
                        Simpan Pengaturan
                    </button>
                </div>

                <div className="space-y-3">
                    <span className="label-eyebrow text-muted-foreground">Logo Toko</span>
                    <div className="aspect-square overflow-hidden rounded-lg border border-border bg-white grid place-items-center p-4">
                        {logoPreview ? (
                            <img src={logoPreview} alt="logo preview" className="max-h-full max-w-full object-contain" data-testid="logo-preview-new" />
                        ) : settings.logo_path ? (
                            <img src={imageUrl(settings.logo_path)} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview-existing" />
                        ) : (
                            <div className="text-center text-muted-foreground">
                                <ImageIcon strokeWidth={1.25} size={36} className="mx-auto" />
                                <p className="mt-2 text-xs">Belum ada logo</p>
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onLogoPick}
                        data-testid="logo-file-input"
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="logo-upload-btn"
                            onClick={() => fileRef.current?.click()}
                            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm hover:bg-secondary transition-all duration-200"
                        >
                            <Upload size={14} strokeWidth={1.75} /> Pilih Logo
                        </button>
                        {logoPreview && (
                            <button
                                type="button"
                                data-testid="logo-remove-btn"
                                onClick={removePending}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-white text-destructive hover:bg-destructive/10 transition-all duration-200"
                                title="Buang"
                            >
                                <Trash2 size={14} strokeWidth={1.75} />
                            </button>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Disarankan PNG transparan, rasio 1:1 (otomatis di-fit ke 512×512).
                    </p>

                    {/* Watermark protection: bulk re-apply logo to all product photos */}
                    <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
                        <div className="flex items-start gap-2.5">
                            <Shield size={16} strokeWidth={1.75} className="text-primary mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground">Perlindungan Watermark</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Foto produk baru otomatis disematkan logo Reno Point di pojok kanan bawah.
                                    Untuk re-apply watermark ke foto produk lama (mis. setelah ganti logo), klik tombol di bawah.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="rewatermark-all-btn"
                            disabled={rewatermarking || !settings.logo_path}
                            onClick={async () => {
                                if (!window.confirm("Apply ulang watermark logo ke SEMUA foto produk yang ada? Ini akan replace foto-foto lama.")) return;
                                setRewatermarking(true);
                                try {
                                    const { data } = await api.post("/admin/products/rewatermark-all");
                                    toast.success(`Watermark diperbarui: ${data.updated}/${data.total} produk` + (data.errors ? ` (${data.errors} error)` : ""));
                                } catch (e) {
                                    toast.error(errorMessage(e));
                                } finally {
                                    setRewatermarking(false);
                                }
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-primary/40 bg-white px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            {rewatermarking ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} strokeWidth={1.75} />}
                            {rewatermarking ? "Memproses..." : "Re-apply Watermark ke Semua Foto"}
                        </button>
                        {!settings.logo_path && (
                            <p className="text-[11px] text-muted-foreground italic">Upload & simpan logo dulu sebelum bisa re-apply watermark.</p>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}
